import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Package, MapPin, Truck, ShoppingBag, Store, CreditCard, Calendar, Download, FileText } from "lucide-react";
import { useOrderById } from "@/hooks/queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { OrderResponseDto } from "@/types/order.dto";
import { TaxInvoiceModal } from "@/components/account/TaxInvoiceModal";

interface TimelineStep {
  label: string;
  isCompleted: boolean;
  timestamp?: string;
  notes?: string;
}

const statusColor: Record<string, string> = {
  Pending: "bg-info/10 text-info border-info/20",
  Confirmed: "bg-warning/10 text-warning border-warning/20",
  Processing: "bg-warning/10 text-warning border-warning/20",
  ReadyForPickup: "bg-primary/10 text-primary border-primary/20",
  OutForDelivery: "bg-primary/10 text-primary border-primary/20",
  Delivered: "bg-success/10 text-success border-success/20",
  Cancelled: "bg-destructive/10 text-destructive border-destructive/20",
  Returned: "bg-destructive/10 text-destructive border-destructive/20",
  Refunded: "bg-destructive/10 text-destructive border-destructive/20",
};

const AccountOrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const orderId = parseInt(id ?? "0", 10);
  const { data: order, isLoading, isError } = useOrderById(orderId);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const getSteps = (order: OrderResponseDto): TimelineStep[] => {
    const history = order.statusHistory || [];
    
    // Helper to format timestamps
    const formatTimestamp = (dateStr: string) => {
      return new Date(dateStr).toLocaleString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      });
    };

    const getStatusTime = (statuses: number[]) => {
      const entry = history.find(h => statuses.includes(h.status));
      return entry ? formatTimestamp(entry.createdAt) : undefined;
    };

    const steps: TimelineStep[] = [];

    // Step 1: Ordered
    steps.push({
      label: "Ordered",
      isCompleted: true,
      timestamp: getStatusTime([0]) || formatTimestamp(order.createdAt), // Pending is 0
    });

    // Step 2: Order packed
    // Processing is 2, Confirmed is 1. If status is >= 1 and not Cancelled (6)
    const isPacked = order.status !== 0 && order.status !== 6;
    steps.push({
      label: "Order packed",
      isCompleted: isPacked,
      timestamp: isPacked ? getStatusTime([1, 2, 3, 4, 5]) : undefined,
    });

    // Step 3: Order left warehouse
    // ReadyForPickup is 3, OutForDelivery is 4, Delivered is 5
    const leftWarehouse = [3, 4, 5].includes(order.status);
    steps.push({
      label: "Order left warehouse",
      isCompleted: leftWarehouse,
      timestamp: leftWarehouse ? getStatusTime([3, 4, 5]) : undefined,
    });

    // Step 4: Dispatched from nearest hub
    // OutForDelivery is 4, Delivered is 5
    const dispatched = [4, 5].includes(order.status);
    steps.push({
      label: "Dispatched from nearest hub",
      isCompleted: dispatched,
      timestamp: dispatched ? getStatusTime([4, 5]) : undefined,
    });

    // Step 5: Delivered or Expected Delivery
    if (order.status === 5) { // Delivered is 5
      steps.push({
        label: "Delivered",
        isCompleted: true,
        timestamp: getStatusTime([5]) || (order.deliveredAt ? formatTimestamp(order.deliveredAt) : undefined),
      });
    } else if ([6, 7, 8].includes(order.status)) { // Cancelled, Returned, Refunded
      const cancelEntry = history.find(h => [6, 7, 8].includes(h.status));
      steps.push({
        label: order.statusText || "Cancelled",
        isCompleted: true,
        timestamp: getStatusTime([6, 7, 8]),
        notes: cancelEntry?.notes || "Your order was cancelled.",
      });
    } else {
      const createdDate = new Date(order.createdAt);
      const estMinutes = order.estimatedDeliveryMinutes ?? 45;
      const expectedDate = new Date(createdDate.getTime() + estMinutes * 60 * 1000);
      const expectedDateStr = expectedDate.toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      
      steps.push({
        label: `Delivery Expected By ${expectedDateStr}`,
        isCompleted: false,
      });
    }

    return steps;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-96 rounded-2xl" />
          </div>
          <div>
            <Skeleton className="h-80 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="text-center py-12 space-y-4">
        <h3 className="text-lg font-semibold">Error Loading Order</h3>
        <p className="text-muted-foreground">We couldn't retrieve the details for this order.</p>
        <Button asChild>
          <Link to="/account/orders">Back to Orders</Link>
        </Button>
      </div>
    );
  }

  const steps = getSteps(order);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" className="rounded-full shrink-0" asChild>
            <Link to="/account/orders">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Order Details</h1>
            <p className="text-sm text-muted-foreground">Order Id - {order.orderNumber}</p>
          </div>
        </div>

        <Button
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl gap-2 shadow-xs h-10 px-4"
          onClick={() => setInvoiceModalOpen(true)}
        >
          <Download className="w-4 h-4" /> Download Tax Invoice
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left/Middle Column: Tracking Timeline and Items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tracking Card */}
          <Card className="border-border/50 shadow-sm">
            <CardHeader className="border-b border-border/40 bg-muted/10">
              <CardTitle className="text-lg font-bold font-heading">Order Tracking</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="relative pl-8 space-y-8">
                {/* Connecting Line */}
                <div className="absolute left-3 top-2.5 bottom-2.5 w-[2px] bg-muted/60" />
                
                {steps.map((step, idx) => (
                  <div key={idx} className="relative flex flex-col items-start">
                    {/* Node Dot */}
                    <div
                      className={`absolute left-[-29px] top-1 z-10 w-[18px] h-[18px] rounded-full border-2 bg-background flex items-center justify-center transition-all ${
                        step.isCompleted
                          ? "bg-success border-success text-success-foreground"
                          : "border-muted-foreground/30 bg-background"
                      }`}
                    >
                      {step.isCompleted && <Check className="h-3 w-3 stroke-[3]" />}
                    </div>
                    
                    {/* Step Content */}
                    <div className="space-y-1">
                      <p
                        className={`text-sm font-bold leading-none ${
                          step.isCompleted ? "text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {step.label}
                      </p>
                      {step.timestamp && (
                        <p className="text-xs text-muted-foreground">{step.timestamp}</p>
                      )}
                      {step.notes && (
                        <p className="text-xs text-destructive mt-1 font-medium bg-destructive/5 px-2 py-1 rounded border border-destructive/10 inline-block">
                          {step.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Items Card */}
          <Card className="border-border/50 shadow-sm">
            <CardHeader className="border-b border-border/40">
              <CardTitle className="text-lg font-bold font-heading">Items Summary</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-border/40 p-0">
              {order.items.map((item) => (
                <div key={item.id} className="flex gap-4 p-4 items-center justify-between">
                  <div className="flex gap-3 items-center">
                    {item.productImage ? (
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        className="w-12 h-12 object-cover rounded-lg border border-border"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-secondary flex items-center justify-center rounded-lg border border-border">
                        <ShoppingBag className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-bold leading-tight">{item.productName}</p>
                      <p className="text-xs text-muted-foreground">Qty: {item.quantity} • ₹{item.unitPrice}</p>
                      {item.variantInfo && (
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5 mt-0.5">
                          {item.variantInfo}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-bold font-heading">₹{item.totalPrice}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Order Summary, Store, and Address Details */}
        <div className="space-y-6">
          {/* Status & Payment Overview */}
          <Card className="border-border/50 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge className={statusColor[order.statusText] || "bg-secondary text-secondary-foreground"}>
                  {order.statusText}
                </Badge>
              </div>
              <div className="flex justify-between items-center border-t border-border/40 pt-3">
                <span className="text-sm text-muted-foreground">Payment</span>
                <span className="text-sm font-semibold">{order.paymentMethodText} ({order.paymentStatusText})</span>
              </div>
              <div className="flex justify-between items-center border-t border-border/40 pt-3">
                <span className="text-sm text-muted-foreground">Placed On</span>
                <span className="text-sm font-semibold">{new Date(order.createdAt).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Details */}
          <Card className="border-border/50 shadow-sm">
            <CardHeader className="border-b border-border/40">
              <CardTitle className="text-base font-bold font-heading">Price Details</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>₹{order.subTotal}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Delivery Charge</span>
                <span>₹{order.deliveryCharge}</span>
              </div>
              {order.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-success font-medium">
                  <span>Discount</span>
                  <span>-₹{order.discountAmount}</span>
                </div>
              )}
              {order.taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>₹{order.taxAmount}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold border-t border-border/40 pt-2.5">
                <span>Total Amount</span>
                <span className="text-primary font-heading">₹{order.totalAmount}</span>
              </div>
            </CardContent>
          </Card>

          {/* Store & Address */}
          <Card className="border-border/50 shadow-sm">
            <CardContent className="p-5 space-y-4">
              {/* Store */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Store Details</p>
                <div className="flex gap-2 items-center">
                  <Store className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-bold">{order.storeName}</span>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="border-t border-border/40 pt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Delivery Address</p>
                <div className="space-y-1">
                  <p className="text-sm font-semibold">{order.shippingAddress.fullName}</p>
                  <p className="text-xs text-muted-foreground">{order.shippingAddress.phone}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                    {order.shippingAddress.addressLine1}
                    {order.shippingAddress.addressLine2 && `, ${order.shippingAddress.addressLine2}`}
                    {order.shippingAddress.landmark && ` (Near ${order.shippingAddress.landmark})`}
                    <br />
                    {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <TaxInvoiceModal
        orderId={orderId}
        open={invoiceModalOpen}
        onOpenChange={setInvoiceModalOpen}
      />
    </div>
  );
};

export default AccountOrderDetail;
